'use client';

import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ContentSearchSettings } from '@/integrations/content-search/content-search-settings/content-search-settings.fetch';

export interface ContentSearchContextState {
  addContentSearchInput: Dispatch<string>;
  contentSearchInput: string;
  contentSearchSettings: ContentSearchSettings;
  recentContentSearches: string[];
  trimRecentContentSearches: Dispatch<number>;
}

const ContentSearchContext = createContext<
  ContentSearchContextState | undefined
>(undefined);

const getLocalStorage = (key: string) => {
  if (typeof window === 'undefined') return [];

  const item = localStorage.getItem(key);

  return item ? JSON.parse(item) : [];
};

const setLocalStorage = (key: string, value: string[]) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(key, JSON.stringify(value));
  }
};

const initialContentSearchSettings: ContentSearchSettings = {
  data: {
    contentSearchTags: [],
    popularSearches: [],
  },
  headers: [],
  parameters: {
    cludoBaseUrl: '',
    customerId: '',
    engineId: '',
    operator: 'or',
    perPage: 10,
  },
};

export const ContentSearchProvider = ({
  children,
  initialData,
}: {
  children: ReactNode;
  initialData?: {
    contentSearchSettings?: ContentSearchSettings;
  };
}) => {
  const [contentSearchInput, setContentSearchInput] = useState('');
  const [recentContentSearches, setRecentContentSearches] = useState<string[]>(
    () => getLocalStorage('recentContentSearches') || [],
  );

  useEffect(() => {
    setLocalStorage('recentContentSearches', recentContentSearches);
  }, [recentContentSearches]);

  const addContentSearchInput = (input: string) => {
    if (!input) return;

    setContentSearchInput(input);

    setRecentContentSearches(previousSearches => {
      const filteredSearches = previousSearches.filter(
        search => search !== input,
      );

      return [input, ...filteredSearches];
    });
  };

  const trimRecentContentSearches = (maxEntries: number) => {
    setRecentContentSearches(previousSearches => {
      return previousSearches.length > maxEntries
        ? previousSearches.slice(0, maxEntries)
        : previousSearches;
    });
  };

  return (
    <ContentSearchContext.Provider
      value={{
        addContentSearchInput,
        contentSearchInput,
        contentSearchSettings:
          initialData?.contentSearchSettings ?? initialContentSearchSettings,
        recentContentSearches,
        trimRecentContentSearches,
      }}
    >
      {children}
    </ContentSearchContext.Provider>
  );
};

export const useContentSearchContext = () => {
  const context = useContext(ContentSearchContext);

  if (!context) {
    throw new Error(
      'useContentSearchContext must be used within a ContentSearchProvider',
    );
  }

  return context;
};
